import { formatDate } from "@angular/common";
import { HttpErrorResponse } from "@angular/common/http";
import { Component, DestroyRef, inject, input, OnInit } from '@angular/core';
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { FormGroup } from "@angular/forms";
import { Router } from "@angular/router";
import { ButtonComponent } from "@controls/button/button.component";
import { AuthService } from "@core/services/auth.service";
import { SigningStatus } from "@models/annual-statements/annual-statement-enums";
import { DocumentRecord } from "@models/annual-statements/document-record";
import { DocumentSubFolderName } from "@models/common/document-sub-folder-name.enum";
import { EntityType } from "@models/common/entity-type.enum";
import { ExportTypeEnum } from "@models/common/export-type.enum";
import { Company } from "@models/companies/company";
import { Entity } from "@models/companies/entity";
import { AsicStatus } from "@models/documents/asic-status.enum";
import { DocumentStatusEnum } from "@models/documents/document-status.enum";
import { NgbDropdown, NgbDropdownItem, NgbDropdownMenu, NgbDropdownToggle, NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { DocumentsExportService } from "@pages/documents/services/documents-export.service";
import { DocumentsGridService } from "@pages/documents/services/documents-grid.service";
import {
  CommonActionsModalComponent,
  IActionButtonConfig
} from "@shared/components/commons/common-actions-modal/common-actions-modal.component";
import { MarkAsLodgedComponent } from "@shared/components/documents-related/mark-as-lodged/mark-as-lodged.component";
import { MarkSignedComponent } from "@shared/components/documents-related/mark-signed/mark-signed.component";
import {
  AgDocumentActionsComponent
} from "@shared/components/grid/ag-cells/documents/ag-document-actions/ag-document-actions.component";
import {
  GridServerSideComponent
} from "@shared/components/grid/components/grid-server-side/grid-server-side.component";
import { NotesComponent } from "@shared/components/notes/notes.component";
import { documentsQueryParamKey } from "@shared/constants/query-param-keys.constant";
import { DocumentsApiService } from "@shared/services/documents-api.service";
import { DocumentCommonService } from "@shared/services/documents-common.service";
import { FilesApiService } from "@shared/services/files-api.service";
import { MarkSentSignedCommonService } from "@shared/services/mark-sent-signed.common.service";
import { OrganisationApiService } from "@shared/services/organisation-api.service";
import { PageSizeService } from "@shared/services/page-size.service";
import { documentStatusToStep } from "@shared/utils/document-status-to-step";
import { downloadBase64File } from "@shared/utils/download-base64-file";
import { excelReportTitleStyles } from "@shared/utils/excel-title-for-report";
import { Guid } from "@shared/utils/guid.helper";
import {
  getGenerateDocumentInvoicePath,
  getGenerateDocumentPath,
  getSharesPagePath
} from "@shared/utils/route-path-utils";
import { ColDef, CsvExportParams, ExcelStyle, GetRowIdFunc, GetRowIdParams, GridApi } from "ag-grid-community";
import { ToastrService } from "ngx-toastr";
import { catchError, EMPTY, filter, map, Observable, of, switchMap, tap } from "rxjs";
import { CompanyMistakeCorrectionComponent } from "../../../../../../forms/asic-forms/company";
import { CompanyMistakeCorrection } from "../../../../../../forms/models/company";
import { CompanyMistakeCorrectionType } from "../../../../../../forms/models/company/company-mistake-correction.model";
import { ModalFormsService } from "../../../../../../forms/services/modal-forms.service";

export type DocumentsGridStorageKey = 'documents'
  | 'company-documents'
  | 'trust-documents';


@Component({
  selector: 'app-documents-grid',
  standalone: true,
  imports: [
    NotesComponent,
    ButtonComponent,
    NgbDropdown,
    NgbDropdownMenu,
    NgbDropdownToggle,
    NgbDropdownItem,
    GridServerSideComponent,
  ],
  providers: [
    DocumentsGridService,
    DocumentsExportService
  ],
  templateUrl: 'documents-grid.component.html',
  styleUrl: 'documents-grid.component.scss'
})
export class DocumentsGridComponent implements OnInit {
  private modalService = inject(NgbModal);
  private documentsApiService = inject(DocumentsApiService);
  private documentsGridService = inject(DocumentsGridService);
  private documentCommonService = inject(DocumentCommonService);
  private documentsExportService = inject(DocumentsExportService);
  private filesApiService = inject(FilesApiService);
  private modalFormsService = inject(ModalFormsService);
  private router = inject(Router);
  private destroyRef = inject(DestroyRef);
  private toastr = inject(ToastrService);
  private authService = inject(AuthService);
  private organisationService = inject(OrganisationApiService);
  private markSentSignedCommonService = inject(MarkSentSignedCommonService);

  entityId = input<string | undefined>(undefined);
  storageKey = input<DocumentsGridStorageKey | null>(null);

  readonly pageTitle = 'Documents';
  readonly EntityType = EntityType;

  form!: FormGroup;
  documentRecords: DocumentRecord[] = [];
  selectedDocuments: DocumentRecord[] = [];
  notesVisible = false;
  entityForNotes: Entity | undefined | null = null;
  documentCanBeOpened = false;

  ngOnInit(): void {
    this.documentCanBeOpened = this.authService.hasRole(['Admin', 'Manager', 'Preparer']);
    this.initGrid();
  }

  mainMenuActionItems = [
    { name: 'Lodge in Bulk', action: () => this.lodgeBulk() },
    { name: 'Mark as Signed in bulk', action: () => this.markAsSignedBulk() },
    { name: 'Mark as Lodged in bulk', action: () => this.markAsLodgedBulk() },
    { name: 'Send for eSign in bulk', action: () => this.bulkSendForSigning() },
    { name: 'Download in bulk', action: () => this.downloadDocumentsBulk() },
    { name: 'Download combined PDF', selectedCount: 1, action: () => this.downloadCombinedPDF() },
    { name: 'Delete in Bulk', action: () => this.deleteDocumentBulk() },
    { name: 'Export List of Documents to xlsx', action: () => this.exportSelectedDocumentsToXls() },
  ];

  getMainMenuAction() {
    const filteredBySelectedCount = this.mainMenuActionItems.filter(item => !item.selectedCount || item.selectedCount === this.selectedDocuments.length);

    return filteredBySelectedCount;
  }

  get showMerge(): boolean {
    return this.documentRecords.filter(x => x.checked && x.type == "484").length > 1;
  }

  async bulkSendForSigning(): Promise<void> {
    if (this.selectedDocuments.some(x => x.status !== DocumentStatusEnum.SignaturePending && x.signingStatus !== SigningStatus.ReadyToSend)) {
      this.toastr.error('Only forms with \'Ready to Send\' signing status can be sent for signing. Please review the selected options and try again');
      return;
    }

    if (!(await this.handleDocumentRecordWithShareIssueBulk())) return;

    const allowedDocumentsRecords = this.selectedDocuments.filter((documentRecord) => !documentRecord.isActionsForbidCauseShareIssue);
    const documentsIds = allowedDocumentsRecords.map((x: DocumentRecord) => x.documentId);
    const linkParams = { queryParams: { [documentsQueryParamKey]: documentsIds } };

    void this.router.navigate(['/bulk-send-documents-for-signing'], linkParams);
  }

  openNotes(document: DocumentRecord): void {
    if (this.modalOpened()) return;
    this.notesVisible = true;
    this.entityForNotes = new Entity({
      entityId: document.entityId,
      name: document.documentName,
      entityNumber: document.entityNumber,
    });
  }

  openDocument(document: DocumentRecord): void {
    if (DocumentRecord.isDocumentInvoice(document)) {
      void this.router.navigate(getGenerateDocumentInvoicePath(document.documentId));
    } else {
      const step = documentStatusToStep(document.status);
      void this.router.navigate(getGenerateDocumentPath(document.documentId), { queryParams: { step } });
    }
  }

  private deleteDocument(document: DocumentRecord): void {
    this.documentCommonService.deleteDocument(document).pipe(
      tap((result: boolean) => {
        if (result) {
          // TODO: replace with analog
          this.documentsGridService.setDataSource('');
        }
      }),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe();
  }


  private deleteDocumentBulk(): void {
    this.documentCommonService.deleteDocumentBulk(this.selectedDocuments).pipe(
      tap((result: boolean) => {
        if (result) {
          this.reloadGrid();
        }
      }),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe();
  }

  downloadDocument(document: DocumentRecord): void {
    this.filesApiService.getFiles(document.documentId).pipe(
      switchMap(res => {
        if (res.final.length) {
          return this.filesApiService.downloadFolder(document.documentId, false, DocumentSubFolderName.Final).pipe(
            tap(res => {
              if (res) {
                const currentDate = formatDate(new Date, 'dd_MM_yyyy', 'en-US');
                const fileName = 'Documents_' + currentDate + '.zip';
                downloadBase64File(res, fileName);
                this.toastr.success("Documents have been successfully downloaded", "Success");
              }
            }),
            catchError((error: HttpErrorResponse) => {
              console.error(error);
              this.toastr.error("Download documents error", "Error");
              return of('ERR');
            })
          );
        }

        this.toastr.warning("No documents for download", "Warning");

        return of(res);
      }),
      catchError((error: HttpErrorResponse) => {
        console.error(error);
        this.toastr.error("Download documents error", "Error");
        return of('ERR');
      }),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe();
  }

  private openForm492(documentRecord: DocumentRecord): void {
    if (this.modalOpened()) return;
    const company = new Company({ entityId: documentRecord.entityId, });
    const instance = this.modalFormsService.openModalWithCompany(new CompanyMistakeCorrection(), company).componentInstance as CompanyMistakeCorrectionComponent;
    instance.documentId = documentRecord.documentId;
  }

  private openForm902(documentRecord: DocumentRecord): void {
    if (this.modalOpened()) return;
    const company = new Company({ entityId: documentRecord.entityId, });
    const instance = this.modalFormsService.openModalWithCompany(new CompanyMistakeCorrection({ correctionType: CompanyMistakeCorrectionType.F902 }), company).componentInstance as CompanyMistakeCorrectionComponent;
    instance.documentId = documentRecord.documentId;
  }

  get modalOpened() {
    return this.modalFormsService.modalOpened;
  }

  async lodge(documentRecord: DocumentRecord): Promise<void> {
    if (documentRecord.isActionsForbidCauseShareIssue && !(await this.handleDocumentRecordWithShareIssue(documentRecord))) {
      return;
    }

    if (!this.isLodgeAllowed([documentRecord])) return;

    const prepareLodge$ = (documentRecord.signingStatus === SigningStatus.Signed
      || documentRecord.signingStatus === SigningStatus.MarkedAsSigned) && documentRecord.dateSigned
      ? of(documentRecord.documentId)
      : this.openMarkSignedModal(documentRecord.earliestChangeDate).pipe(
        switchMap(signatureDate => {
          return this.documentsApiService.updateSignDate([documentRecord.documentId], signatureDate).pipe(
            tap(() => this.documentsGridService.gridApi?.refreshServerSide())
          );
        }),
        map(() => documentRecord.documentId)
      );

    prepareLodge$
      .pipe(
        switchMap(id => this.documentsApiService.lodgeDocument([id])),
        tap(() => {
          this.toastr.success('Document sent to lodge', 'Success');
        }),
        catchError(() => {
          this.toastr.error('Error while sending document to lodge', 'Error');
          return EMPTY;
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        complete: () => {this.reloadGrid();}
      });
  }

  private openMarkSignedModal(earliestChangeDate: Date | null): Observable<string> {
    const modalRef = this.modalService.open(MarkSignedComponent, { size: 'lg' });
    (modalRef.componentInstance as MarkSignedComponent).earliestLodgeDate = earliestChangeDate ? new Date(earliestChangeDate) : undefined;

    return (modalRef.componentInstance as MarkSignedComponent).confirm;
  }

  async lodgeBulk(): Promise<void> {
    if (!this.isLodgeAllowed(this.selectedDocuments)) return;

    if (!(await this.handleDocumentRecordWithShareIssueBulk())) {
      return;
    }

    const allowedDocumentsRecords = this.selectedDocuments.filter((documentRecord) => !documentRecord.isActionsForbidCauseShareIssue);
    const selectedIds = allowedDocumentsRecords.map(x => x.documentId);

    const isNotSignedPresent = allowedDocumentsRecords.some(x => x.signingStatus != SigningStatus.Signed && x.signingStatus != SigningStatus.MarkedAsSigned || !x.dateSigned);
    const earliestChangeDate = allowedDocumentsRecords.filter(x => x.earliestChangeDate).map(x => x.earliestChangeDate).sort()[0];
    const documentsIdToSetSignDate = allowedDocumentsRecords.filter(x => !x.dateSigned).map(x => x.documentId);

    const prepareLodge$ = !isNotSignedPresent
      ? of(selectedIds)
      : this.openMarkSignedModal(earliestChangeDate).pipe(
        switchMap(signatureDate => {
          return this.documentsApiService.updateSignDate(documentsIdToSetSignDate, signatureDate).pipe(
            tap(() => this.documentsGridService.gridApi?.refreshServerSide())
          );
        }),
        map(() => selectedIds)
      );

    prepareLodge$
      .pipe(
        switchMap(ids => this.documentsApiService.lodgeDocument(ids)),
        tap(() => {
          this.toastr.success('Document sent to lodge', 'Success');
        }),
        catchError(() => {
          this.toastr.error('Error while sending document to lodge', 'Error');
          return EMPTY;
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        complete: () => this.reloadGrid()
      });
  }

  isLodgeAllowed(documentRecords: DocumentRecord[]): boolean {
    const notAllowedToLodgeForms = ["c:106", "c:211", "c:280", "c:281", "c:2205"];
    if (documentRecords.some(x => notAllowedToLodgeForms.includes(x.type))) {
      this.toastr.error("Error: Forms 106, 2205, 211, 280 and 281 cannot be lodged. Please review selected options and try again.", "Error");
      return false;
    }

    if (documentRecords.some(x => x.status != DocumentStatusEnum.LodgementPending && x.status != DocumentStatusEnum.SignaturePending || x.asicStatus != AsicStatus.NotLodged)) {
      this.toastr.error("Only forms with 'Lodgement Pending' document status can be lodged. Please review the selected options and try again", "Error");
      return false;
    }

    return true;
  }

  async markAsSigned(documentRecord: DocumentRecord) {
    if (documentRecord.isActionsForbidCauseShareIssue && !(await this.handleDocumentRecordWithShareIssue(documentRecord))) {
      return;
    }

    if (this.isMarkAsSignedAllowed([documentRecord]) === false)
      return;

    const inSignedProcess = documentRecord.signingStatus === SigningStatus.Sent;

    this.markSentSignedCommonService.markAsSigneDoc(documentRecord.documentId, inSignedProcess, documentRecord.earliestChangeDate).subscribe({
      next: () => {
        this.reloadGrid();
      }
    });

  }

  async markAsSent(documentRecord: DocumentRecord): Promise<void> {
    if (documentRecord.isActionsForbidCauseShareIssue && !(await this.handleDocumentRecordWithShareIssue(documentRecord))) {
      return;
    }

    this.markSentSignedCommonService.markAsSent([documentRecord.documentId], true)
      .pipe(
        filter(result => result === true),
        tap(() => {
          this.toastr.success("Document was marked as sent", "Success");
          this.reloadGrid();
        }))
      .subscribe();
  }

  async markAsSignedBulk() {
    if (this.isMarkAsSignedAllowed(this.selectedDocuments) === false) return;

    if (!(await this.handleDocumentRecordWithShareIssueBulk())) return;

    const allowedDocumentsRecords = this.selectedDocuments.filter((documentRecord) => !documentRecord.isActionsForbidCauseShareIssue);
    const selectedIds = allowedDocumentsRecords.map((x: DocumentRecord) => x.documentId);
    const earliestChangeDate = allowedDocumentsRecords.filter(x => x.earliestChangeDate).map(x => x.earliestChangeDate).sort()[0];
    const inSignedProcess = allowedDocumentsRecords.some(x => x.signingStatus === SigningStatus.Sent);

    this.markSentSignedCommonService.markAsSignedDocumentBulk(selectedIds, inSignedProcess, earliestChangeDate).subscribe({
      next: () => {
        this.reloadGrid();
      }
    });

  }

  isMarkAsSignedAllowed(documentRecords: DocumentRecord[]) {
    if (documentRecords.some(x => (x.status !== DocumentStatusEnum.SignaturePending && x.status !== DocumentStatusEnum.LodgementPending) || (x.signingStatus !== undefined
      && ![SigningStatus.Sent, SigningStatus.ReadyToSend, SigningStatus.SentViaEmail, SigningStatus.MarkedAsSent].includes(x.signingStatus)))) {
      this.toastr.error('Only forms with \'Ready to Send\' or \'Sent\' signing status can be marked as signed. Please review the selected options and try again');
      return false;
    }

    return true;
  }

  async markAsLodged(documentRecord: DocumentRecord): Promise<void> {
    if (documentRecord.isActionsForbidCauseShareIssue && !(await this.handleDocumentRecordWithShareIssue(documentRecord))) {
      return;
    }

    if (documentRecord.asicStatus != AsicStatus.NotLodged || documentRecord.status != DocumentStatusEnum.LodgementPending) {
      this.toastr.error('Only forms with \'Not Lodged\' ASIC status and \'LodgementPending\' document status can be marked as lodged. Please review the selected options and try again.');
      return;
    }

    const modalRef = this.modalService.open(MarkAsLodgedComponent, { size: 'sm', centered: true });
    const instance = modalRef.componentInstance as MarkAsLodgedComponent;

    instance.confirm.pipe(
      switchMap((number) =>
        this.documentsApiService.markAsLodge([documentRecord.documentId], number).pipe(
          tap(() => {
            this.reloadGrid();
            this.toastr.success("Document was marked as lodged", "Success");
          })
        )
      ),
      catchError(() => {
        this.toastr.error("Error while marking as lodged", "Error");
        return EMPTY;
      }),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe();
  }

  async markAsLodgedBulk(): Promise<void> {
    if (this.selectedDocuments.some(x => x.asicStatus != AsicStatus.NotLodged || x.status != DocumentStatusEnum.LodgementPending)) {
      this.toastr.error('Only forms with \'Not Lodged\' ASIC status and \'LodgementPending\' document status can be marked as lodged. Please review the selected options and try again.');
      return;
    }

    if (!(await this.handleDocumentRecordWithShareIssueBulk())) return;

    const allowedDocumentRecords = this.selectedDocuments.filter((documentRecord) => !documentRecord.isActionsForbidCauseShareIssue);
    const selectedIds = allowedDocumentRecords.map((x: DocumentRecord) => x.documentId);

    const modalRef = this.modalService.open(MarkAsLodgedComponent, { size: 'sm', centered: true });
    const instance = modalRef.componentInstance as MarkAsLodgedComponent;

    instance.confirm
      .pipe(
        switchMap((number) => this.documentsApiService.markAsLodge(selectedIds, number)
          .pipe(
            tap(() => {
              this.reloadGrid();
              this.toastr.success("Document was marked as lodged", "Success");
            })
          )
        ),
        catchError(() => {
          this.toastr.error("Error while marking as lodged", "Error");
          return EMPTY;
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  protected downloadDocumentsBulk(): void {
    const selectedIds = this.selectedDocuments.map(x => x.documentId);
    this.filesApiService.downloadDocumentBulk(selectedIds).pipe(
      tap(res => {
        if (res) {
          const currentDate = formatDate(new Date, 'dd_MM_yyyy', 'en-US');
          const fileName = 'Documents_' + currentDate + '.zip';
          downloadBase64File(res, fileName);
          this.toastr.success("Documents have been successfully downloaded", "Success");
        }
      }),
      catchError((error: HttpErrorResponse) => {
        console.error(error);
        this.toastr.error("Download Documents error", "Error");
        return of('ERR');
      })
    ).subscribe();
  }

  downloadCombinedPDF(): void {
    const selectedIds = this.selectedDocuments.map(x => x.documentId);
    if (selectedIds.length > 1) {
      this.toastr.warning("Only one document is allowed to download as combined", "Warning");
    }

    this.filesApiService.downloadFolder(selectedIds[0], true, DocumentSubFolderName.Final).pipe(
      tap(res => {
        if (res) {
          const currentDate = formatDate(new Date, 'dd_MM_yyyy', 'en-US');
          const fileName = 'Documents_' + currentDate + '.pdf';
          downloadBase64File(res, fileName);
          this.toastr.success("Document have been success downloaded", "Success");
        }
      }),
      catchError((error: HttpErrorResponse) => {
        console.error(error);
        this.toastr.error("Download Document error", "Error");
        return of('ERR');
      })
    ).subscribe();
  }

  handleDocumentRecordWithShareIssue(documentRecord: DocumentRecord): Promise<boolean> {
    const body = `
        <div class="text-center">Share issues have been identified for this company.</div>
        <div class="text-center">Click ‘Resolve’ to review and fix the details.</div>
    `;

    const actions: IActionButtonConfig[] = [
      {
        text: 'Resolve',
        iconClass: 'icon ic-resolve-20',
        action: of(true)
      }, {
        text: 'Cancel',
        btnColor: 'white',
        action: of(false)
      },
    ];

    const modalRef = this.modalFormsService.openModal(CommonActionsModalComponent, { size: 'sm', centered: true });
    const instance = modalRef.componentInstance as CommonActionsModalComponent;

    instance['forceClose'] = true;
    instance.config = {
      ...instance.config,
      actions,
      iconClass: 'ic-alert-triangle-20 bg-red',
      header: 'Share Issues Found',
      body,
    };

    return modalRef.result.then(
      (result: boolean) => {
        const res = result ?? false;
        this.modalFormsService.closeAllFormsModals();

        if (res) {
          void this.router.navigate(getSharesPagePath(documentRecord.entityId));
        }

        return false;
      },
      (result: boolean) => {
        this.modalFormsService.closeAllFormsModals();
        return false;
      }
    );
  }

  handleDocumentRecordWithShareIssueBulk(records: DocumentRecord[] = this.selectedDocuments): Promise<boolean> {
    const isIncludeForbidRecords = records.some((record) => record.isActionsForbidCauseShareIssue);

    if (!isIncludeForbidRecords) {
      return Promise.resolve(true);
    }

    const companyNames: Record<string, string> = {};
    const companyForbidDescriptions: Record<string, string[]> = {};

    records
      .filter((record) => record.isActionsForbidCauseShareIssue)
      .forEach((record) => {
        companyNames[record.entityId] = record.documentName;

        if (companyForbidDescriptions[record.entityId]) {
          companyForbidDescriptions[record.entityId].push(record.description);
        } else {
          companyForbidDescriptions[record.entityId] = [record.description];
        }
      });

    const companyNamesListItems: string[] = [];

    Object.entries(companyNames).forEach(([entityId, companyName]) => {
      const companyDescriptionsListItems: string[] = [];

      if (companyForbidDescriptions[entityId]) {
        companyForbidDescriptions[entityId].forEach((documentRecordDescription) => {
          const companyDescriptionListItem = `<li>${ documentRecordDescription }</li>`;
          companyDescriptionsListItems.push(companyDescriptionListItem);
        });
      }

      const companyNameListItem = `
          <li>
            <div>${ companyName }</div>
            <ul>${ companyDescriptionsListItems.join('') }</ul>
          </li>
        `;

      companyNamesListItems.push(companyNameListItem);
    });

    const body = `
        <div>Share issues have been identified for some of the selected companies.</div>
        <div>Please note that selected documents for the will not be processed in bulk as share issues were found for their associated companies:</div>
        <ul class="mb-0 mt-3 ps-3">${ companyNamesListItems.join('') }</ul>
    `;
    const actions: IActionButtonConfig[] = [
      {
        text: 'OK',
        action: of(true)
      },
      {
        text: 'Cancel',
        btnColor: 'white',
        action: of(false)
      }
    ];

    const modalRef = this.modalFormsService.openModal(CommonActionsModalComponent, { size: 'sm', centered: true });
    const instance = modalRef.componentInstance as CommonActionsModalComponent;

    instance['forceClose'] = true;
    instance.config = {
      ...instance.config,
      actions,
      iconClass: 'ic-alert-triangle-20 bg-red',
      header: 'Share Issues Found',
      body,
    };

    return modalRef.result.then(
      (result: boolean) => {
        this.modalFormsService.closeAllFormsModals();
        return result ?? false;
      },
      () => {
        this.modalFormsService.closeAllFormsModals();
        return false;
      }
    );
  }

  // =====  Grid  =====

  readonly pinFirstColumnName = 'documentName';
  readonly cacheBlockSize = 20;
  readonly defaultPageSize = PageSizeService.defaultPageSize;

  colDefs = this.documentsGridService.colDefs;
  totalRecords = this.documentsGridService.totalRecords;
  dataSource = this.documentsGridService.dataSource;

  getRowId: GetRowIdFunc = (params: GetRowIdParams<DocumentRecord>) => params.data.documentId;

  onSearch(searchText: string): void {
    this.documentsGridService.setDataSource(searchText);
  }

  onGridReady(gridApi: GridApi): void {
    this.documentsGridService.setGridApi(gridApi);
  }

  selectDocuments(documents: DocumentRecord[]): void {
    this.selectedDocuments = documents;
  }

  initGrid(): void {
    this.documentsGridService.setGridFilterOptions(
      this.documentCanBeOpened,
      this.organisationService.getCachedProfileUsers().map(u => u?.fullName)
    );

    const isEntityDocuments = this.entityId() && Guid.isValidGuid(this.entityId()!);
    const actionColumn: ColDef = {
      width: 120,
      sortable: false,
      cellRenderer: AgDocumentActionsComponent,
      cellRendererParams: {
        openDocument: this.openDocument.bind(this),
        openNotes: this.openNotes.bind(this),
        deleteDocument: this.deleteDocument.bind(this),
        openForm492: this.openForm492.bind(this),
        openForm902: this.openForm902.bind(this),
        downloadDocument: this.downloadDocument.bind(this),
        markAsSigned: this.markAsSigned.bind(this),
        markAsLodged: this.markAsLodged.bind(this),
        markAsSent: this.markAsSent.bind(this),
        lodge: this.lodge.bind(this),

      },
      menuTabs: []
    };

    if (isEntityDocuments) {
      this.documentsGridService.setEntityDocumentsColDefs(actionColumn, this.entityId()!);
    } else {
      this.documentsGridService.setDocumentsColDefs(actionColumn);
    }
  }

  reloadGrid(): void {
    this.documentsGridService.gridApi?.refreshServerSide();
    this.documentsGridService.gridApi.deselectAll();
  }

  // =====  Export  =====

  readonly ExportTypeEnum = ExportTypeEnum;
  readonly excelExportParams = this.documentsExportService.exportParamsXls();
  readonly csvExportParams = this.documentsExportService.getExportParams() as CsvExportParams;
  readonly excelStyles: ExcelStyle[] = excelReportTitleStyles;

  exportSelectedDocumentsToXls(): void {
    this.exportDocumentsList(true, ExportTypeEnum.EXCEL);
  }

  exportDocumentsList(isBulkExport: boolean, exportType: ExportTypeEnum): void {
    if (this.modalOpened()) return;

    this.documentsExportService.exportDocumentsList(
      this.selectedDocuments,
      isBulkExport,
      exportType
    );
  }
}
